// Общие date-хелперы ленты чата стилиста (FR-1…FR-4 фичи chat-timestamps-and-model-label).
// Бэкенд отдаёт createdAt строкой ISO-8601 с offset (UTC); строка без offset — легаси/кэш —
// трактуется как UTC, чтобы не показывать «серверное» время как локальное.

import {plural} from './plural';

/** Есть ли в ISO-строке явная зона: Z либо ±HH:MM / ±HHMM. */
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;

/** Парсер ISO-времени бэкенда: строка без offset трактуется как UTC; битая строка → null. */
export const parseIsoUtc = (iso?: string | null): Date | null => {
    if (!iso) return null;
    const d = new Date(HAS_OFFSET.test(iso) ? iso : `${iso}Z`);
    return isNaN(d.getTime()) ? null : d;
};

/** Время сообщения «HH:mm» в локальной зоне пользователя; пусто для битой даты. */
export const fmtTime = (iso?: string | null): string => {
    const d = parseIsoUtc(iso);
    return d ? d.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'}) : '';
};

/** Формат списка тредов: «дд.мм, чч:мм» — прежний формат StylistPage. */
export const fmtDayMonthTime = (iso?: string | null): string => {
    const d = parseIsoUtc(iso);
    return d
        ? d.toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
        : '';
};

/** Один календарный день в локальной зоне пользователя. */
export const isSameCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Подпись чипа-разделителя даты в стиле Telegram (FR-2): «Сегодня» / «Вчера» /
 * «d MMMM» (ru-RU), для другого года — «d MMMM yyyy». Битая дата → null.
 */
export const dayChipLabel = (iso?: string | null, now: Date = new Date()): string | null => {
    const d = parseIsoUtc(iso);
    if (!d) return null;
    if (isSameCalendarDay(d, now)) return 'Сегодня';
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (isSameCalendarDay(d, yesterday)) return 'Вчера';
    const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
        ? {day: 'numeric', month: 'long'}
        : {day: 'numeric', month: 'long', year: 'numeric'};
    return d.toLocaleDateString('ru-RU', opts);
};

/**
 * Сколько календарных суток назад произошло событие в зоне пользователя. Считается по датам,
 * а не по длительности: вчерашние 23:50 — это «вчера», хотя с тех пор прошло меньше суток.
 */
export const calendarDaysAgo = (date: Date, now: Date = new Date()): number => {
    const then = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.round((today - then) / 86_400_000);
};

/**
 * Относительная подпись даты события: «Сегодня», «Вчера», «2 дня назад» — и короткая дата,
 * начиная с недельной давности. Сутки календарные ({@link calendarDaysAgo}), поэтому подпись
 * в шапке вещи совпадает с датой в блоке «Использование». Будущая дата подписывается датой.
 */
export const relativeDayRu = (iso?: string | null, now: Date = new Date()): string | null => {
    const date = parseIsoUtc(iso);
    if (!date) return null;
    const days = calendarDaysAgo(date, now);
    if (days === 0) return 'Сегодня';
    if (days === 1) return 'Вчера';
    if (days > 1 && days < 7) return `${plural(days, 'день', 'дня', 'дней')} назад`;
    return date.toLocaleDateString('ru-RU');
};
